import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { accessLevelsApi } from '../accessLevelsApi';
import type { AccessLevel } from '../accessLevelTypes';
import { AccessLevelModal } from '../components/AccessLevelModal';

/**
 * The clearance ladder — the rungs a record can be classified at and an account tagged with.
 *
 * The anchor opens the **profile**, not the edit form: every index page in this product is
 * partnered with one, and here the profile is where the comparison a rank stands for is spelled
 * out — "rank 2" means nothing on its own, and getting it backwards is the whole failure mode.
 *
 * The account count is on the row and the NAMES are not: naming who holds a clearance belongs on
 * the account, where somebody can act on it.
 */
export function AccessLevelsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<AccessLevel | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    level: AccessLevel;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    The ranks already in use, for the form.

    Fetched separately from the list because the list is PAGED: a rank clash is against the
    whole ladder, and checking it against the thirty rows on screen would pass for a clash on
    page two and then be refused by the server. It also feeds Autogenerate, which has to fill a
    rank the server will accept or the button leaves Submit disabled.
  */
  const [takenRanks, setTakenRanks] = useState<number[]>([]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    accessLevelsApi
      .options(controller.signal)
      .then((levels) => setTakenRanks(levels.map((l) => l.rank)))
      // A failed fetch leaves the list empty, so the form falls back to the server's own
      // refusal. It must not take the page down.
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchLevels = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      accessLevelsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (level: AccessLevel): MenuItem[] => [
      {
        key: `edit-${level.id}`,
        label: 'Edit',
        permission: 'access-levels.update',
        onClick: () => setEditing(level),
      },
      {
        key: `restore-${level.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'access-levels.delete',
        disabledReason: level.isActive ? 'This access level is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', level }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await accessLevelsApi.deactivate(confirming.level.id);
      } else {
        await accessLevelsApi.activate(confirming.level.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<AccessLevel>
        module="access-levels"
        breadcrumb={['Administration', 'Access Levels']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['AccessLevel', 'UserAccessLevel']}
        rowKey={(level) => level.id}
        fetch={fetchLevels}
        reloadKey={reloadKey}
        searchPlaceholder="Search access levels"
        emptyMessage="No access levels yet. Until there is one, nothing can be classified and every reader sees every record."
        onCreate={() => setCreating(true)}
        createPermission="access-levels.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(level) => navigate(`/access-levels/${level.id}`)}
        openPermission="access-levels.read"
        rowActions={rowActions}
        onDelete={(level) => setConfirming({ kind: 'deactivate', level })}
        deletePermission="access-levels.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(level) =>
          level.isActive ? null : 'This access level is already deleted.'
        }
        onClearData={() => accessLevelsApi.clear()}
        clearDataPermission="access-levels.clear-data"
        otherInfo={(level) => level}
      />

      {creating || editing !== null ? (
        <AccessLevelModal
          open
          editing={editing}
          takenRanks={takenRanks}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete access level' : 'Restore access level'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`access-level-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.level.name}" stops being offered when a record is classified or an account tagged.`
                  : `"${confirming.level.name}" is offered again when a record is classified or an account tagged.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  Records already classified at this level stay classified, and the{' '}
                  {confirming.level.userCount} account
                  {confirming.level.userCount === 1 ? '' : 's'} tagged with it keep their
                  clearance. Deleting a rung decides what can be chosen next, never what has
                  already been decided.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted access level can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<AccessLevel>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 180,
    flex: true,
    sortable: true,
    render: (level) => <TextCell value={level.name} />,
  },
  {
    /*
      The rank, under a heading that says what the number means. It is sortable and it is the
      list's default order, because the ladder's own order is the only one that reads correctly
      — sorted by name it would run "Level 1, Level 10, Level 2".
    */
    key: 'rank',
    header: 'Rank',
    width: 90,
    align: 'right',
    sortable: true,
    summary: true,
    render: (level) => <TextCell value={String(level.rank)} mono numeric />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (level) => <TextCell value={level.description} />,
  },
  {
    key: 'userCount',
    header: 'Accounts',
    width: 110,
    align: 'right',
    summary: true,
    // The count is of accounts, which is another module's data — the rung beside it is this
    // module's own and stays ungated.
    permission: 'users.read',
    render: (level) => <TextCell value={String(level.userCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (level) =>
      level.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
